import type { KeyEventArgs } from "./events.js";

/** What the desktop's own window keys do. */
export type WindowCommand = "close" | "next" | "previous" | "systemMenu";

/** For each command, the chords that run it; null or an empty list turns the command off. */
export type WindowKeyMap = Partial<Record<WindowCommand, readonly string[] | null>>;

/**
 * Win32's MDI keys, because a page in a browser is in the position an MDI child is: the
 * browser owns Alt+F4, Alt+Tab and Alt+Space. Ctrl+F4 is listed although a browser tab
 * usually takes it first; an installed app window or a kiosk passes it through.
 */
export const DEFAULT_WINDOW_KEYS: Readonly<Record<WindowCommand, readonly string[]>> = {
  close: ["Ctrl+F4"],
  next: ["Ctrl+F6"],
  previous: ["Ctrl+Shift+F6"],
  systemMenu: ["Alt+-", "Alt+Space"],
};

interface Chord {
  key: string;
  ctrl: boolean;
  shift: boolean;
  alt: boolean;
  meta: boolean;
}

/** `"Ctrl+Shift+F6"` as parts. Modifier names are case-insensitive; `Space` is " ". */
export function parseChord(text: string): Chord {
  const parts = text.split("+");
  // "Alt++" ends in an empty part for the plus key itself.
  let key = parts.pop() ?? "";
  if (key === "" && parts.length > 0 && parts[parts.length - 1] === "") {
    parts.pop();
    key = "+";
  }
  const mods = new Set(parts.map((p) => p.trim().toLowerCase()));
  return {
    key: key.toLowerCase() === "space" ? " " : key,
    ctrl: mods.has("ctrl") || mods.has("control"),
    shift: mods.has("shift"),
    alt: mods.has("alt"),
    meta: mods.has("meta") || mods.has("cmd") || mods.has("win"),
  };
}

/** Whether `args` is `chord`, with exactly its modifiers held. */
export function matchesChord(chord: string, args: KeyEventArgs): boolean {
  const c = parseChord(chord);
  if (args.ctrlKey !== c.ctrl || args.altKey !== c.alt || args.metaKey !== c.meta) return false;
  // Shift is part of what a printable key reports, so "Alt+-" is not refused for a layout
  // that needs Shift to type the hyphen; a named key holds Shift to what was asked.
  const printable = c.key.length === 1;
  if (!printable && args.shiftKey !== c.shift) return false;
  if (printable && c.shift && !args.shiftKey) return false;
  return args.key.length === 1 && printable ? args.key.toLowerCase() === c.key.toLowerCase() : args.key === c.key;
}

/** The command `args` runs under `map` laid over the defaults, or null. */
export function windowCommandFor(args: KeyEventArgs, map: WindowKeyMap = {}): WindowCommand | null {
  for (const command of Object.keys(DEFAULT_WINDOW_KEYS) as WindowCommand[]) {
    const chords = command in map ? map[command] : DEFAULT_WINDOW_KEYS[command];
    if (chords?.some((chord) => matchesChord(chord, args))) return command;
  }
  return null;
}
